import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { TRY, NUM, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { pickOcc, trUp } from "@/components/returns/util";
import { LatestBadge } from "@/components/returns/PriceHistory";

const SHOW = 200;

/** Tedarikçiden alınan tüm ürünler (en son alış faturasıyla) — işaretleyip toplu ekleme. */
export function SupplierCatalog({ vkn, ver, exclude, onAddMany }) {
  const [groups, setGroups] = useState(null);
  const [f, setF] = useState("");
  const [sel, setSel] = useState(() => new Set());

  useEffect(() => {
    setGroups(null); setSel(new Set());
    if (!vkn) return;
    api.get("/qnb/returns/search", { params: { vkn, q: "", limit: 1000, exclude: exclude || undefined } })
      .then((r) => setGroups(r.data.groups)).catch((e) => { toast.error(apiError(e)); setGroups([]); });
  }, [vkn, ver, exclude]);

  const rows = useMemo(() => {
    const toks = trUp(f).split(/\s+/).filter(Boolean);
    return (groups || []).filter((g) => { const s = trUp(`${g.name} ${g.code}`); return toks.every((t) => s.includes(t)); });
  }, [groups, f]);

  if (!vkn) return <div className="rounded-xl border border-dashed py-8 text-center text-sm text-muted-foreground" data-testid="ret-catalog-nosupplier">Listeyi görmek için yukarıdan tedarikçi seçin.</div>;
  if (!groups) return <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  const toggle = (k) => setSel((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const add = () => { onAddMany(groups.filter((g) => sel.has(g.key))); setSel(new Set()); };

  return (
    <div className="space-y-2" data-testid="ret-catalog">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={f} onChange={(e) => setF(e.target.value)} placeholder={`${groups.length} ürün içinde ara`} className="h-9 pl-9" data-testid="ret-catalog-filter" />
        </div>
        <Button onClick={add} disabled={!sel.size} className="bg-orange-600 hover:bg-orange-700 h-9" data-testid="ret-catalog-add"><Plus className="h-4 w-4 mr-1" />Seçilenleri ekle ({sel.size})</Button>
      </div>
      <div className="rounded-xl border max-h-[320px] overflow-y-auto divide-y">
        {rows.slice(0, SHOW).map((g, i) => {
          const o = pickOcc(g);
          return (
            <label key={g.key} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`ret-catalog-row-${i}`}>
              <Checkbox checked={sel.has(g.key)} onCheckedChange={() => toggle(g.key)} data-testid={`ret-catalog-check-${i}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold truncate">{g.name}</div>
                <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2">
                  {g.code && <span className="font-mono">{g.code}</span>}
                  {o && <span className="inline-flex items-center gap-1"><LatestBadge />{fmtDate(o.date)} · <span className="font-mono">{o.belge_no}</span> · <b className="text-foreground/80">{NUM(o.quantity)} {g.unit} alındı</b></span>}
                  {g.count > 1 && <span>{g.count} fatura · önceki fiyat {TRY(g.occurrences[1].net_unit)}</span>}
                </div>
              </div>
              {o && <span className="text-[13px] font-bold tabular-nums whitespace-nowrap text-emerald-700">{TRY(o.net_unit)}</span>}
            </label>
          );
        })}
        {rows.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Eşleşen ürün yok</div>}
        {rows.length > SHOW && <div className="py-2 text-center text-[11px] text-muted-foreground">İlk {SHOW} ürün gösteriliyor — aramayı daraltın</div>}
      </div>
    </div>
  );
}
